import { Injectable, Signal, inject, signal } from '@angular/core';

import { AUTH_SERVICE_TOKEN } from '../auth-service/auth.service';

export interface RecentClient {
  userId: string;
  firstName: string;
  lastName: string;
  viewedAt: number;
}

interface RecentClientsStorage {
  version: number;
  entries: RecentClient[];
}

/**
 * Tracks the clients the advisor viewed most recently, persisted in localStorage
 * per advisor. Only stable data (id, name, timestamp) is stored — profile picture
 * URLs are presigned and short-lived, so they are re-fetched from the backend at
 * render time instead.
 */
@Injectable({
  providedIn: 'root',
})
export class RecentlyViewedClientsService {
  private static readonly MAX_ENTRIES = 20;
  private static readonly STORAGE_VERSION = 1;

  private readonly authService = inject(AUTH_SERVICE_TOKEN);
  private readonly clientsSignal = signal<RecentClient[]>(this.loadFromStorage());

  public get clients(): Signal<RecentClient[]> {
    return this.clientsSignal.asReadonly();
  }

  public record(client: { userId: string; firstName: string; lastName: string }): void {
    const entry: RecentClient = {
      userId: client.userId,
      firstName: client.firstName,
      lastName: client.lastName,
      viewedAt: Date.now(),
    };
    const entries: RecentClient[] = [
      entry,
      ...this.clientsSignal().filter((existing: RecentClient): boolean => existing.userId !== client.userId),
    ].slice(0, RecentlyViewedClientsService.MAX_ENTRIES);

    this.update(entries);
  }

  public remove(userId: string): void {
    this.update(this.clientsSignal().filter((entry: RecentClient): boolean => entry.userId !== userId));
  }

  private update(entries: RecentClient[]): void {
    this.clientsSignal.set(entries);
    try {
      const storage: RecentClientsStorage = { version: RecentlyViewedClientsService.STORAGE_VERSION, entries };
      localStorage.setItem(this.storageKey(), JSON.stringify(storage));
    } catch {
      // Storage full or unavailable — the in-memory list still works for this session
    }
  }

  private loadFromStorage(): RecentClient[] {
    try {
      const raw: string | null = localStorage.getItem(this.storageKey());
      if (!raw) {
        return [];
      }
      const storage: RecentClientsStorage = JSON.parse(raw);
      if (storage.version !== RecentlyViewedClientsService.STORAGE_VERSION || !Array.isArray(storage.entries)) {
        return [];
      }
      return storage.entries.slice(0, RecentlyViewedClientsService.MAX_ENTRIES);
    } catch {
      return [];
    }
  }

  private storageKey(): string {
    return `recent-clients:${this.authService.userId}`;
  }
}
